<template>
  <!-- 🎯 COMING SOON PAGE -->
  <div class="coming-soon-container">
    <div class="coming-soon-content">
      <div class="coming-soon-icon">🏗️</div>
      <h2 class="coming-soon-title">Schedule Tracking</h2>
      <p class="coming-soon-subtitle">Coming Soon</p>
      <p class="coming-soon-description">
        We're building comprehensive schedule tracking and variance analysis features.<br>
        This will include Gantt charts, milestone tracking, and schedule performance metrics.
      </p>
      <div class="coming-soon-features">
        <div class="feature-item">
          <span class="feature-icon">📊</span>
          <span class="feature-text">Schedule Variance Analysis</span>
        </div>
        <div class="feature-item">
          <span class="feature-icon">📅</span>
          <span class="feature-text">Milestone Tracking</span>
        </div>
        <div class="feature-item">
          <span class="feature-icon">⏱️</span>
          <span class="feature-text">Duration Forecasting</span>
        </div>
        <div class="feature-item">
          <span class="feature-icon">📈</span>
          <span class="feature-text">Progress Visualization</span>
        </div>
      </div>
    </div>
  </div>

  <!-- ORIGINAL SCHEDULE CONTENT (HIDDEN) -->
  <div v-if="false && isDataLoaded && !isNoDataFetch" class="schedule-container">
    <div class="schedule-summary-header-container">
      <div class="schedule-summary-header-title">Schedule Summary</div>
    </div>
    <div class="chart-container">
      <!-- Hard Costs stacked bar -->
      <div class="title-container">
        <div class="chart-title">Schedule Variance Summary</div>
      </div>
      <div class="stacked-charts-container">
        <div class="chart hc-offenders-stacked-bar">
          <div class="chart-subtitle top-offenders-subtitle">
            Hard Costs
          </div>
          <Bar :height="350" :width="1550" :data="hardCostsStackedBarData" :options="hardCostsStackedBarOptions" />
        </div>
      </div>
    </div>

    <div class="schedule-tracking-header-container">
      <div class="schedule-tracking-header-title">Schedule Tracking</div>
    </div>
    <div class="grid-container schedule-gantt" id="scheduleViewGrid">
      <ag-grid :row-height="30" :row-data="dataShow" :column-defs="localColumnDefs" :column-options="columnOptions"
        :tree-data="true" :full-scroll="true" custom-column="buttonGantt" :key="dataShow.length"
        :auto-group-column-def="autoGroupColumnDef" @setChartData="setChartData" :groupDefaultExpanded="2"/>
      <ToolTip :is-show="tooltipIsShow" :top="`${tooltipTop}px`" :left="`${tooltipLeft}px`" :html="tooltipHtml" />
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { standardStackedBarChartJson } from "@/assets/js/chart_config";
import { axiosQuery, decimalPart, formatDecimals, numberFormat, percentFormat } from "@/assets/js/utils";
import { cleanupHierarchyNames } from "@/data/data";
import { useAuthStore } from "@/stores/auth_store";
import { useErrorStore } from "@/stores/error_store";
import { useGanttTooltipStore } from "@/stores/gantt_tooltip_store";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Colors,
  Legend,
  LinearScale,
  LogarithmicScale,
  Title,
  Tooltip,
} from "chart.js";
import { computed, onMounted, reactive, ref } from "vue";
import { Bar } from "vue-chartjs"; // note this is a publicly available Chart JS wrapper, MIT licensed
import AgGrid from "../../components/AgGrid.vue";
import ToolTip from "../../components/helpers/ToolTip.vue";
import GanttCellRenderer from "../../components/renderers/GanttCellRenderer.vue";
import { useRoute } from 'vue-router';

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Colors,
  ArcElement,
  LogarithmicScale
);

// Column Settings in AG Grid
let localColumnDefs = [
  {
    headerName: 'Scopes of Work',
    headerClass: 'center-align-header',
    children: [
      {
        headerName: 'Company',
        field: 'col2', // TODO: Will go back to this once we have the data coming from the DB
        cellStyle: { color: '#999', backgroundColor: '#f0f0f0' },
        headerClass: 'disabled-header',
        sortable: false,
        filter: false,
        editable: false,
        resizable: false,
        minWidth: 150,
      }
    ],
  },
  {
    headerName: "Durations",
    children: [
      {
        headerName: "Planned Duration",
        field: "projected_project_months",
        aggFunc: avgAggregate,
        headerClass: 'header-custom-lilac',
        editable: false,
        valueParser: "Number(newValue)",
        minWidth: 110,
        type: 'rightAligned'
      },
      {
        headerName: "Actual Duration",
        field: "actual_project_months",
        aggFunc: avgAggregate,
        headerClass: 'header-custom-lilac',
        editable: false,
        valueParser: "Number(newValue)",
        minWidth: 110,
        type: 'rightAligned'
      },
      {
        headerName: "Variance",
        field: "variance",
        aggFunc: varianceAggregate,
        headerClass: 'header-custom-lightblue',
        editable: false,
        valueParser: "Number(newValue)",
        minWidth: 100,
        cellRenderer: (params => {
          if (params.value !== 0 && !params.value) {
            return ''
          }
          return formatDecimals(params.value)
        }),
        type: 'rightAligned'
      },
    ]
  }
]
const columnOptions = {
  actual_project_months: {
    minWidth: 110,
  },
  projected_project_months: {
    minWidth: 110,
  },
  // The default ag_grid_field_helper makes a percent, so fix that here
  variance: {
    minWidth: 100,
    cellRenderer: (params => {
      if (params.value !== 0 && !params.value) {
        return ''
      }
      return formatDecimals(params.value)
    })
  },
}
const autoGroupColumnDef = {
  headerName: 'Description of Work',
  minWidth: 350,
  pinned: 'left', // freeze/pin itemHierarchy to the left so it can be seen when scrolling (esp. for Schedule page)
  cellRendererParams: {
    suppressCount: true,
  },
}

const tooltipStore = useGanttTooltipStore()
let tooltipIsShow = ref(tooltipStore.getIsShow)
let tooltipTop = ref(tooltipStore.getTop)
let tooltipLeft = ref(tooltipStore.getLeft)
let tooltipHtml = ref(tooltipStore.getHtml)
tooltipStore.$subscribe(updateTooltip, { detached: false })
function updateTooltip() {
  tooltipIsShow.value = tooltipStore.getIsShow
  tooltipTop.value = tooltipStore.getTop
  tooltipLeft.value = tooltipStore.getLeft
  tooltipHtml.value = tooltipStore.getHtml
}

// Month Settings in Ag Grid
const maxMonths = 48
let columnGroup = {}
Array(maxMonths).fill(0).forEach((_, month) => {
  if (month % 12 === 0) {
    if (month > 0) {
      // save old column defs
      localColumnDefs.push(columnGroup)
    }
    columnGroup = {
      groupId: `year_${month / 12 + 1}`,
      headerName: `Year ${month / 12 + 1}`,
      menuTabs: ['columnsMenuTab'],
      children: []
    }
  }
  columnGroup.children.push({
    headerName: `${month % 12 + 1}`,
    field: `month_length_change_${month}`,
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      suppressCount: true,
      checkbox: true,
      // This innerRenderer is used by group items and then the params are as defined by cellRendererSelector
      // Without this innerRenderer here, the group items will not show the gantt chart
      innerRenderer: 'GanttCellRenderer',
      suppressDoubleClickExpand: true,
      suppressEnterExpand: true,
    },
    // This function does the aggregates for group rows. That array gets stored internally by AgGrid as params.value
    // and is used by cellRendererSelector
    aggFunc: (params => monthAggregate(params, month)),
    editable: false,
    // columnGroupShow: 'open', // Does not work unless we have a column that is open on the left...
    suppressHeaderMenuButton: true,
    sortable: false,
    suppressMovable: true,
    // this seems to help with the far right, though 20 allows us to see the 48th month better
    maxWidth: 34, // month > 45 ? 30 : 20,
    headerClass: month % 12 === 0 ? 'schedule-sparkline-header year' : 'schedule-sparkline-header',
    cellClass: month % 12 === 0 ? 'schedule-sparkline year' : 'schedule-sparkline',
    cellRendererSelector: (params) => {
      return {
        component: GanttCellRenderer,
        params: {
          topWidthPct: parseFloat(params.value[0]),
          bottomWidthPct: parseFloat(params.value[1]),
          isTopRightAligned: params.value[2],
          isBottomRightAligned: params.value[3],
          projectedProjectMonths: formatDecimals(parseFloat(params.value[4]), 1),
          actualProjectMonths: formatDecimals(parseFloat(params.value[5]), 1),
          // -0.00 sometimes crops up here...
          projectedMonthsIn: formatDecimals(Math.abs(parseFloat(params.value[6])), 1),
          actualMonthsIn: formatDecimals(Math.abs(parseFloat(params.value[7])), 1),
        },
      }
    },
    filter: false,
  })
})
// save last set of column Defs
localColumnDefs.push(columnGroup)

let isDataLoaded = computed(() => {
  return isAveragedDataLoaded.value //&& isScheduleOffendersDataLoaded.value;
})
let isNoDataFetch = ref(false);

// Hard Costs Stacked Bar Chart
const labelMaxLength = 15;
const hardCostsStackedBarData = computed(() => {
  return {
    labels: schedStackedBarLabels.value,
    datasets: [
      {
        label: "Projected",
        backgroundColor: "#4A5FD5", // $brightBlue1
        data: projectedProjectMonthsArr.value,
      },
      {
        label: "Sched Variance",
        backgroundColor: "#84CADE", // $lightBlue1
        data: schedVarianceArr.value,
      },
    ],
  };
});

let hardStackedBarJsonConfig = standardStackedBarChartJson({
  barPercentage: 0.5,
  xTicksFontSize: 12,
  xTicksFont: 'Inter',
  labelForcedLength: labelMaxLength,
})

const hardCostsStackedBarOptions = computed(() => {
  let json = hardStackedBarJsonConfig
  // override some configurations
  json.indexAxis = "y"
  json.scales.y = {
    stacked: true,
    display: true
  }
  json.scales.x.ticks = {
    font: "10",
    family: "Inter",
    stepSize: "1",
    beginAtZero: true,
    autoSkip: false,
    callback: function (label) {
      return label
    }
  }
  // See documentation for lineWidth. Decided not to use major ticks just for ease, but didn't try them out.
  // 1. https://www.chartjs.org/docs/latest/axes/styling.html
  // 2. https://www.chartjs.org/docs/latest/general/options.html#tick
  json.scales.x.grid = {
    color: '#eee',
    lineWidth: function (context) {
      const index = context.index;
      return (index > 0 && index % 12 === 0) ? '3' : '1'
    },
    drawOnChartArea: true,
  }
  json.scales.y.ticks = {
    font: {
      size: (tick) => (tick?.tick?.label.includes('Total') ? "14" : "10"),
      family: (tick) => (tick?.tick?.label.includes('Total') ? "Inter-Bold" : "Inter"),
    },
  }
  json.scales.y.grid = {
    drawOnChartArea: false
  }
  json.plugins.legend = {
    labels: {
      boxWidth: 10,
    },
    display: true,
    position: "top",
    align: "end"
  }
  json.responsive = true
  json.maintainAspectRatio = true
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.x;
      const index = context.dataIndex;
      let otherLabel = ''
      if (label === 'Projected') {
        otherLabel = `Sched Variance: ${formatDecimals(schedVarianceArr.value[index])} `
      } else {
        otherLabel = `Projected: ${formatDecimals(projectedProjectMonthsArr.value[index])} `
      }
      return label + ": " + formatDecimals(value) + ` (${otherLabel})`;
    },
    footer: function (context) {
      const index = context[0].dataIndex;
      let varianceLabel = ''
      varianceLabel = `Variance: ${percentFormat(schedVariancePercArr.value[index])}`
      return (
        varianceLabel
      );
    },
  }
  return json
})

// Fetch Values
const apiBase =
  process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/";
const authStore = useAuthStore();
const selectedId = ref();
let authToken = reactive({});
const route = useRoute();

const errorStore = useErrorStore();
let aggregateData = {};
let processedAggregateData = {};
let lastProjectsQuery = []
const dataShow = ref([]);
// this is fetching data for all cost types
// Process the data for the summed data
function fetchGridData() {
  // Do *not* requery data
  if (lastProjectsQuery === selectedId.value) {
    return
  } else {
    lastProjectsQuery = selectedId.value
  }

  axiosQuery(
    `${apiBase}scope_of_works/summed_dates?project_ids=${selectedId.value}`,
    authToken,
    (data) => {
      if (data.length == 0) {
        isNoDataFetch.value = true
      } else {
        aggregateData = {}
        processedAggregateData = {}
        data?.forEach((row) => {
          if (row.itemHierarchy && row.actual_project_months) {
            let months = {}
            Array(maxMonths).fill(0).forEach((_, month) => {
              // TODO || parts here are to aid in testing. Need more projected start dates/months
              let projectedStartMonth = parseFloat(row.projected_months_into_project_start || row.actual_months_into_project_start)
              // TODO uncertain why some are < 0
              if (projectedStartMonth < 0) projectedStartMonth = 1
              let projectedEndMonth = projectedStartMonth + parseFloat(row.projected_project_months || row.actual_project_months)
              let projectedContribution = projectedStartMonth <= (month + 1) && projectedEndMonth >= month + 2 ? 1 : 0

              let actualStartMonth = parseFloat(row.actual_months_into_project_start)
              let actualEndMonth = actualStartMonth + parseFloat(row.actual_project_months)
              let actualContribution = actualStartMonth <= (month + 1) && actualEndMonth >= month + 2 ? 1 : 0

              // Get percentage into month -- divided into 10 pieces
              if (parseFloat(numberFormat(projectedStartMonth, 0)) === month) {
                projectedContribution = decimalPart(projectedStartMonth)
                // months[`month_length_change_${month}_projected_start`] = true
              }
              if (parseFloat(numberFormat(actualStartMonth, 0)) === month) {
                actualContribution = decimalPart(actualStartMonth)
                // months[`month_length_change_${month}_actual_start`] = true
              }
              // If we are on the last month....
              if (parseFloat(numberFormat(projectedContribution, 0)) === month + 1) {
                projectedContribution = decimalPart(projectedContribution)
              }
              if (parseFloat(numberFormat(actualContribution, 0)) === month + 1) {
                actualContribution = decimalPart(actualContribution)
              }
              months[`month_length_change_${month}`] = [projectedContribution, actualContribution]
            })

            cleanupHierarchyNames(row.itemHierarchy).forEach(term => {
              aggregateData[term] ||= []
              aggregateData[term].push({
                projected_project_months: numberFormat(row.projected_project_months),
                actual_project_months: numberFormat(row.actual_project_months),
                projected_months_into_project_start: numberFormat(row.projected_months_into_project_start),
                actual_months_into_project_start: numberFormat(row.actual_months_into_project_start),
                variance: numberFormat((row.actual_project_months || 0) - (row.projected_project_months || 0)),
                ...months
              })
            })
          }
        });

        // Process the aggregate data for the subtotal/total rows in the itemHierarchy
        processRollupData()
        // Process the averaged data for the leaf nodes
        processLeafNodeData(data)
        isNoDataFetch.value = false
      }
    },
    errorStore
  );
}

// Chart Value Settings
let schedStackedBarLabels = ref([]);
let projectedProjectMonthsArr = ref([]);
let actualProjectMonthsArr = ref([]);
let schedVarianceArr = ref([]);
let schedVariancePercArr = ref([]);
let isScheduleOffendersDataLoaded = ref(false)

// Pull schedule data from the grid
function setChartData(event) {
  let chartData = event;

  if (chartData) {
    const HC_GROUP_ID = "row-group-0-HARD COSTS"
    let hcGroupData = chartData.find(row => row.id === HC_GROUP_ID);
    // Create a copy of the leaf children
    const hcGroupDataLeafChildren = hcGroupData?.allLeafChildren ?? [];

    // Sort the values base on variances to get the top offenders in decreasing order
    const sortedHCGroupData = hcGroupDataLeafChildren.sort((a, b) => (b.data.variance != null ? b.data.variance : 0)
      - (a.data.variance != null ? a.data.variance : 0));


    // Get the top 5 but remove the ones if they include a key that should not be there
    const top5DataHC = [];

    // // Loop through the sorted array for hard cost
    for (const obj of sortedHCGroupData) {
      top5DataHC.push(obj);

      // If top5DataHC array has 5 elements, break the loop
      if (top5DataHC.length === 5) {
        break;
      }
    }

    // build the chart data
    // get the totals first
    let totalProjectedMonths = hcGroupData ? hcGroupData['aggData']['projected_project_months'] : 0
    let totalActualProjectMonths = hcGroupData ? hcGroupData['aggData']['actual_project_months'] : 0
    let totalSchedVariance = totalActualProjectMonths - totalProjectedMonths
    let totalSchedVariancePerc = totalProjectedMonths > 0 ? (totalActualProjectMonths - totalProjectedMonths) / (totalProjectedMonths || 1) : 0

    schedStackedBarLabels.value = ['Total'];
    projectedProjectMonthsArr.value = [totalProjectedMonths];
    actualProjectMonthsArr.value = [totalActualProjectMonths];
    schedVarianceArr.value = [totalSchedVariance];
    schedVariancePercArr.value = [totalSchedVariancePerc];

    top5DataHC.forEach((row) => {
      let currLabel = row.key
      if (currLabel.length > 12) {
        currLabel = currLabel.slice(0, labelMaxLength) + "...";
      }
      schedStackedBarLabels.value.push(currLabel);
      projectedProjectMonthsArr.value.push(row.data.projected_project_months);
      actualProjectMonthsArr.value.push(row.data.actual_project_months);
      schedVarianceArr.value.push(row.data.actual_project_months - row.data.projected_project_months);
      schedVariancePercArr.value.push((row.data.actual_project_months - row.data.projected_project_months) / (row.data.projected_project_months || 1));
    });
    isScheduleOffendersDataLoaded.value = true;
  } else {
    // Handle the case where chartData is not an array or it's empty
    isScheduleOffendersDataLoaded.value = false;
  }
}

// Functions for process the data and formatting the data
// The goal is to store in the rolled up terms:
// 1. minimum months into project start, as we want to see the earliest it is projected to or actually started
// 2. maximum length of project. This needs to be the latest the project could/did end minus the earliest it could/did start
function processRollupData() {
  Object.keys(aggregateData).forEach(term => {
    let minActualMonthsIntoProject = 0.0;
    let minProjectedMonthsIntoProject = 0.0;
    let maxActualProjectMonthsLength = 0.0;
    let maxProjectedProjectMonthsLength = 0.0;

    if (aggregateData[term]?.length) {
      aggregateData[term].forEach(value => {
        if (minActualMonthsIntoProject) {
          minActualMonthsIntoProject = Math.min(minActualMonthsIntoProject, parseFloat(value.actual_months_into_project_start || 0.0))
        } else {
          minActualMonthsIntoProject = parseFloat(value.actual_months_into_project_start || 0.0)
        }
      })

      aggregateData[term].forEach(value => {
        if (minProjectedMonthsIntoProject) {
          minProjectedMonthsIntoProject = Math.min(minProjectedMonthsIntoProject, parseFloat(value.projected_months_into_project_start || 0.0))
        } else {
          minProjectedMonthsIntoProject = parseFloat(value.projected_months_into_project_start || 0.0)
        }
      })

      aggregateData[term].forEach(value => {
        const actualProjectLength = parseFloat(value.actual_months_into_project_start || 0.0) + parseFloat(value.actual_project_months || 0.0)
        if (maxActualProjectMonthsLength) {
          maxActualProjectMonthsLength = Math.max(maxActualProjectMonthsLength, actualProjectLength)
        } else {
          maxActualProjectMonthsLength = actualProjectLength
        }
      })
      maxActualProjectMonthsLength -= minActualMonthsIntoProject

      aggregateData[term].forEach(value => {
        const projectedProjectLength = parseFloat(value.projected_months_into_project_start || 0.0) + parseFloat(value.projected_project_months || 0.0)
        if (maxProjectedProjectMonthsLength) {
          maxProjectedProjectMonthsLength = Math.max(maxProjectedProjectMonthsLength, projectedProjectLength)
        } else {
          maxProjectedProjectMonthsLength = projectedProjectLength
        }
      })
      maxProjectedProjectMonthsLength -= minProjectedMonthsIntoProject

      // TODO minor hack for now to make data look good since we don't always have projected months
      if (!maxProjectedProjectMonthsLength) { maxProjectedProjectMonthsLength = maxActualProjectMonthsLength }
    }

    processedAggregateData[term] = {
      minActualMonthsIntoProject: minActualMonthsIntoProject,
      minProjectedMonthsIntoProject: minProjectedMonthsIntoProject,
      maxActualProjectMonthsLength: maxActualProjectMonthsLength,
      maxProjectedProjectMonthsLength: maxProjectedProjectMonthsLength
    }
  })
}

// Process the data for the individual nodes
let isAveragedDataLoaded = ref(false)
function processLeafNodeData(data) {
  data?.forEach((row) => {
    if (row.itemHierarchy && row.actual_project_months) {
      let months = {}
      Array(maxMonths).fill(0).forEach((_, month) => {
        // TODO || parts here are to aid in testing. Need more projected start dates/months
        let projectedStartMonth = parseFloat(row.projected_months_into_project_start || row.actual_months_into_project_start)
        let projectedProjectMonths = parseFloat(row.projected_project_months || row.actual_project_months)
        // TODO uncertain why some are < 0
        if (projectedStartMonth < 0) projectedStartMonth = 1

        let actualStartMonth = parseFloat(row.actual_months_into_project_start)
        let actualProjectMonths = parseFloat(row.actual_project_months)

        let [projectedWidthPct, isProjectedRightAligned] = monthPercent(month, projectedStartMonth, projectedProjectMonths)
        let [actualWidthPct, isActualRightAligned] = monthPercent(month, actualStartMonth, actualProjectMonths)

        months[`month_length_change_${month}`] = [projectedWidthPct, actualWidthPct, isProjectedRightAligned,
          isActualRightAligned, projectedProjectMonths, actualProjectMonths, projectedStartMonth, actualStartMonth]
      })
      dataShow.value.push({
        ...{
          id: row.id,
          project_id: row.project_id,
          dda_term_id: row.dda_term_id,
          hiddenHierarchy: cleanupHierarchyNames(row.itemHierarchy),
          itemHierarchy: cleanupHierarchyNames(row.itemHierarchy),
          projected_project_months: formatDecimals(row.projected_project_months),
          actual_project_months: formatDecimals(row.actual_project_months),
          projected_months_into_project_start: formatDecimals(row.projected_months_into_project_start),
          actual_months_into_project_start: formatDecimals(row.actual_months_into_project_start),
          variance: formatDecimals((row.actual_project_months || 0) - (row.projected_project_months || 0)),
        }, ...months
      })
    }
  });
  isAveragedDataLoaded.value = true
}

function avgAggregate(params) {
  if (!processedAggregateData[params.rowNode.key]) { return null }
  if (params.column.colId === 'projected_project_months') {
    return formatDecimals((processedAggregateData[params.rowNode.key].maxProjectedProjectMonthsLength || 0))
  } else if (params.column.colId === 'actual_project_months') {
    return formatDecimals((processedAggregateData[params.rowNode.key].maxActualProjectMonthsLength || 0))
  } else {
    // Should not get here!
    return null
  }
}
// Uses the sum aggregates so they can be used to calculate variance
// NOTE: this only works because the sum aggregate columns are calculated first!
function varianceAggregate(params) {
  if (!processedAggregateData[params.rowNode.key]) { return null }
  const projected = (processedAggregateData[params.rowNode.key].maxProjectedProjectMonthsLength || 0)
  const actual = (processedAggregateData[params.rowNode.key].maxActualProjectMonthsLength || 0)

  // Not a percent
  return formatDecimals(((actual || 0.0) - (projected || 0.0))) // / (parseFloat(originalBudget) || 1.0))
}

// Aggregate to show in column for this month
// Depending on which data is passed in, the goal is the get:
// 1. minimum months into project start, as we want to see the earliest it is projected to or actually started
// 2. maximum length of project. This needs to be the latest the project could/did end minus the earliest it could/did start
function monthAggregate(params, month) {
  let data = processedAggregateData[params.rowNode.key]

  if (data) {
    let [projectedWidthPct, isProjectedRightAligned] = monthPercent(month, data.minProjectedMonthsIntoProject, data.maxProjectedProjectMonthsLength)
    let [actualWidthPct, isActualRightAligned] = monthPercent(month, data.minActualMonthsIntoProject, data.maxActualProjectMonthsLength)

    return [projectedWidthPct, actualWidthPct, isProjectedRightAligned, isActualRightAligned,
      data.maxProjectedProjectMonthsLength, data.maxActualProjectMonthsLength, data.minProjectedMonthsIntoProject, data.minActualMonthsIntoProject]
  } else {
    return Array(8).fill(0)
  }
}

// Calculates how much of a band to show of a project length
function monthPercent(month, monthsIntoProject, projectMonthLength) {
  let widthPct = 0
  let isRightAligned = false

  if (monthsIntoProject < month + 1) {
    if (monthsIntoProject > month) {
      // spans part of the first column cell
      widthPct = (month + 1) - monthsIntoProject
      isRightAligned = true
    } else if (monthsIntoProject + projectMonthLength > month &&
      monthsIntoProject + projectMonthLength < month + 1) {
      // spans part of the end column cell
      widthPct = (monthsIntoProject + projectMonthLength) - (month)
    } else if (monthsIntoProject <= (month + 2) && monthsIntoProject + projectMonthLength > month) {
      widthPct = 1
    }
  }

  return [widthPct, isRightAligned]
}

function updateData() {
  fetchGridData()
}

onMounted(() => {
  authToken.value = authStore.getAuthTokenHeader
  selectedId.value = route.params.selectedId;
  // Enable auth to load fully before updating data (not sure if this completely makes sense)
  setTimeout(() => {
    updateData()
  }, 0)
});

</script>
<style scoped lang="scss">
@import "./src/assets/main.scss";

.schedule-gantt .schedule-sparkline {
  // keep border, but as narrow as possible
  border-left-width: 0.5px;
  border-right-width: 0.5px;
  outline: 0;
}

.schedule-gantt .schedule-sparkline.year {
  // keep border slightly wider at the year cutoff
  border-left-width: 1.5px;
}

.schedule-gantt .schedule-sparkline-header {
  // keep sparklines flush against cells
  box-sizing: content-box;
  padding-right: 0;
  padding-left: 4px;
}

.schedule-gantt .schedule-sparkline-header.year {
  // keep border slightly wider at the year cutoff
  border-left-width: 1.5px;
}

.ag-sparkline-tooltip-wrapper .schedule-sparkline-tooltip {
  color: white;
  border: 10px solid $darkBlue1;
  border-radius: 10px;
  background-color: $darkBlue1;
  font-family: $specialFont;
  font-size: 11px;
}

.schedule-container {
  display: flex;
  flex-direction: column;
}

.chart-container {
  flex: 0.5;
}

.grid-container {
  flex: 1;
}

.chart-container {
  display: flex;
  flex-direction: column;
  background: $white1;
  border: 1px solid $white1;
  box-shadow: 6px 0 18px rgba(0, 0, 0, 0.06);
  border-radius: 15px;
  width: 96.5%;
  margin: 0 10px;
}

.chart-title,
.chart-subtitle {
  margin: 10px 15px 15px 15px;
  font-family: $specialFontBold;
}

.chart-title {
  font-size: 16px;
}

.chart-subtitle {
  font-size: 14px;
}

.top-offenders-subtitle {
  visibility: visible;
}

.title-container,
.stacked-charts-container {
  display: flex;
  flex-direction: column;
}

.title-container {
  width: 100%;
}

.stacked-charts-container {
  overflow-x: scroll;
  margin: 0 20px 0 20px;
}

/* Schedule Summary Style - Start */
.schedule-summary-header-container {
  margin: 30px 0 20px 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  .schedule-summary-header-title {
    font-size: 22px;
    font-weight: bolder;
    color: $navyBlue;
    margin-right: 20px;
  }
}

/* Schedule Summary Style - End */

/* Schedule Tracking Style - Start */
.schedule-tracking-header-container {
  margin: 30px 0 10px 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;

  .schedule-tracking-header-title {
    font-size: 22px;
    font-weight: bolder;
    color: $navyBlue;
    margin-right: 20px;
  }
}

/* Schedule Tracking Style - End */

/* AG Grid Styles - Start */
.disabled-header {
  color: #999 !important;
  background-color: #f0f0f0 !important;
  cursor: not-allowed;
  pointer-events: none;
}

/* AG Grid Styles - End */

/* 🎯 COMING SOON PAGE STYLES */
.coming-soon-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 600px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  padding: 40px;
}

.coming-soon-content {
  text-align: center;
  background: white;
  padding: 60px 80px;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  max-width: 700px;
}

.coming-soon-icon {
  font-size: 80px;
  margin-bottom: 20px;
  animation: bounce 2s infinite;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-20px);
  }
}

.coming-soon-title {
  font-size: 36px;
  font-weight: 700;
  color: #1a2d4d;
  margin: 0 0 10px 0;
}

.coming-soon-subtitle {
  font-size: 24px;
  font-weight: 600;
  color: #3b82f6;
  margin: 0 0 20px 0;
  text-transform: uppercase;
  letter-spacing: 2px;
}

.coming-soon-description {
  font-size: 16px;
  color: #718096;
  line-height: 1.6;
  margin: 0 0 40px 0;
}

.coming-soon-features {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 40px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #f8fafc;
  border-radius: 12px;
  border-left: 4px solid #3b82f6;
  transition: all 0.3s ease;
}

.feature-item:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
}

.feature-icon {
  font-size: 24px;
}

.feature-text {
  font-size: 15px;
  font-weight: 600;
  color: #1a2d4d;
}

</style>
